/**
 * CustomerCare 组件
 * 职责: 管理邮件模板和售后卡
 *
 * 重构后结构:
 * - types.ts: 类型定义
 * - hooks/: 自定义 Hooks
 * - components/: 子组件
 */

import React, { useState, useEffect } from 'react';
import { Tabs, Form, App } from 'antd';
import { MailOutlined, FileTextOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import axios from 'axios';
import { CustomerCareForm, CustomerCareList } from './components';
import type { CustomerCareProps, CustomerCareRecord, CareType } from './types';

const { TabPane } = Tabs;

export const CustomerCare: React.FC<CustomerCareProps> = ({ skuId }) => {
    const [form] = Form.useForm();
    const { message, modal } = App.useApp();
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState<CustomerCareRecord[]>([]);
    const [editingRecord, setEditingRecord] = useState<CustomerCareRecord | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [activeTab, setActiveTab] = useState<CareType>('email');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadRecords();
    }, [skuId]);

    const loadRecords = async () => {
        try {
            const response = await axios.get(`/api/customer-care/?sku_id=${skuId}`);
            const data = Array.isArray(response.data) ? response.data : response.data.results || [];
            setRecords(data);
        } catch (error) {
            console.error('加载客户关怀记录失败:', error);
        }
    };

    const handleUpload = async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post('/api/customer-care/upload_file/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data;
        } catch (error) {
            message.error('文件上传失败');
            throw error;
        }
    };

    const handleDeleteFile = async (filePath: string) => {
        try {
            await axios.delete('/api/customer-care/delete_file/', {
                data: { path: filePath },
            });
        } catch (error) {
            console.error('删除文件失败:', error);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const newFiles = fileList.filter(file => file.originFileObj);
            const existingFiles = fileList.filter(file => !file.originFileObj && file.url);

            const uploadedFiles = await Promise.all(newFiles.map(file => handleUpload(file.originFileObj!)));

            const existingAttachments = existingFiles.map(
                file => file.response || { filename: file.name, path: file.uid, url: file.url! }
            );

            const attachments = [...existingAttachments, ...uploadedFiles];

            const data = {
                sku: skuId,
                care_type: activeTab,
                email_type: values.email_type || '',
                subject: values.subject || '',
                content: values.content,
                notes: values.notes || '',
                attachments: attachments,
            };

            if (editingRecord) {
                await axios.patch(`/api/customer-care/${editingRecord.id}/`, data);
                message.success('更新成功');
            } else {
                await axios.post('/api/customer-care/', data);
                message.success('保存成功');
            }

            form.resetFields();
            setFileList([]);
            setEditingRecord(null);
            loadRecords();
        } catch (error) {
            console.error('保存失败:', error);
            message.error('保存失败');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (record: CustomerCareRecord) => {
        setEditingRecord(record);
        setActiveTab(record.care_type);
        form.setFieldsValue({
            email_type: record.email_type,
            subject: record.subject,
            content: record.content,
            notes: record.notes,
        });
        const existingFiles: UploadFile[] = record.attachments.map((att, idx) => ({
            uid: `-existing-${idx}`,
            name: att.filename,
            status: 'done',
            url: att.url,
            response: att,
        }));
        setFileList(existingFiles);
    };

    const handleDelete = (record: CustomerCareRecord) => {
        modal.confirm({
            title: '确认删除',
            content: '确定要删除这条记录吗？',
            onOk: async () => {
                try {
                    for (const att of record.attachments) {
                        await handleDeleteFile(att.path);
                    }
                    await axios.delete(`/api/customer-care/${record.id}/`);
                    message.success('删除成功');
                    loadRecords();
                } catch (error) {
                    message.error('删除失败');
                }
            },
        });
    };

    const cancelEdit = () => {
        setEditingRecord(null);
        form.resetFields();
        setFileList([]);
    };

    const emailRecords = records.filter(r => r.care_type === 'email');
    const cardRecords = records.filter(r => r.care_type === 'after_sales_card');

    return (
        <div className="customer-care">
            <Tabs activeKey={activeTab} onChange={key => setActiveTab(key as CareType)}>
                <TabPane
                    tab={
                        <span>
                            <MailOutlined />
                            邮件模板
                        </span>
                    }
                    key="email"
                >
                    <CustomerCareForm
                        careType="email"
                        form={form}
                        loading={loading}
                        editingRecord={editingRecord}
                        fileList={fileList}
                        onFileListChange={setFileList}
                        onSubmit={handleSubmit}
                        onCancelEdit={cancelEdit}
                    />
                    <CustomerCareList
                        records={emailRecords}
                        icon={<MailOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <FileTextOutlined />
                            售后卡
                        </span>
                    }
                    key="after_sales_card"
                >
                    <CustomerCareForm
                        careType="after_sales_card"
                        form={form}
                        loading={loading}
                        editingRecord={editingRecord}
                        fileList={fileList}
                        onFileListChange={setFileList}
                        onSubmit={handleSubmit}
                        onCancelEdit={cancelEdit}
                    />
                    <CustomerCareList
                        records={cardRecords}
                        icon={<FileTextOutlined style={{ fontSize: 24, color: '#52c41a' }} />}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                </TabPane>
            </Tabs>
        </div>
    );
};

export default CustomerCare;
